import { useState, useEffect, useCallback } from 'react';
import { Municipality } from '../../administration/type';
import dataProvider from '../../services/api';

interface ErrorHandlers {
  handleErrorWithLogout: (error: any) => void;
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
}

export const useMunicipalities = (
  currentPage: number, 
  municipalitiesPerPage: number, 
  debouncedSearchMunicipality: string, 
  currentVoucherCityIds: number[] = [],
  shouldRefetch: boolean = true,
  errorHandlers: ErrorHandlers
) => {
  const [allMunicipalities, setAllMunicipalities] = useState<Municipality[]>([]);
  const [availableMunicipalities, setAvailableMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalMunicipalities, setTotalMunicipalities] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAllMunicipalities, setTotalAllMunicipalities] = useState(0);

  const { handleErrorWithLogout, showSuccessMessage, showErrorMessage } = errorHandlers;

  const fetchAllMunicipalities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const initialResult = await dataProvider.getAllMunicipalities('administrator', {
        count: 1,
        page: 0
      });
      
      const total = initialResult.total;
      setTotalAllMunicipalities(total);
      
      const fullResult = await dataProvider.getAllMunicipalities('administrator', {
        count: total,
        page: 0
      });
      
      setAllMunicipalities(fullResult.data);
    } catch (err) {
      setError('Failed to fetch all municipalities');
      handleErrorWithLogout(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchMunicipalities = async () => {
      if (allMunicipalities.length === 0 || !shouldRefetch) return;

      try {
        setError(null);

        if (debouncedSearchMunicipality.trim()) {
          const result = await dataProvider.lookupCities('administrator', {
            name: debouncedSearchMunicipality,
            count: municipalitiesPerPage,
            page: currentPage - 1
          });
          
          setAvailableMunicipalities(result.data);
          setTotalMunicipalities(result.total || 0);
          setTotalPages(Math.ceil((result.total || 0) / municipalitiesPerPage));
        } else {
          const assignedIds = currentVoucherCityIds || [];
          const unassignedMunicipalities = allMunicipalities.filter(m => !assignedIds.includes(m.id));
          
          const startIndex = (currentPage - 1) * municipalitiesPerPage;
          const endIndex = startIndex + municipalitiesPerPage;
          const paginatedMunicipalities = unassignedMunicipalities.slice(startIndex, endIndex);
          
          setAvailableMunicipalities(paginatedMunicipalities);
          setTotalMunicipalities(unassignedMunicipalities.length);
          setTotalPages(Math.ceil(unassignedMunicipalities.length / municipalitiesPerPage));
        }
      } catch (err) {
        setError('Failed to fetch municipalities');
        handleErrorWithLogout(err);
      }
    };

    fetchMunicipalities();
  }, [debouncedSearchMunicipality, currentPage, municipalitiesPerPage, allMunicipalities, currentVoucherCityIds, shouldRefetch]);

  useEffect(() => {
    fetchAllMunicipalities();
  }, []);

  const retryFetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchAllMunicipalities();
      showSuccessMessage('Municipalities loaded successfully');
    } catch (err) {
      setError('Failed to fetch cities');
      handleErrorWithLogout(err);
    }
  }, []);

  return {
    allMunicipalities,
    availableMunicipalities,
    loading,
    error,
    totalMunicipalities,
    totalPages,
    totalAllMunicipalities,
    retryFetch
  };
};